import { defineStore } from 'pinia';
import { ref } from 'vue';
import { searchApi } from '@/services/SearchApi';
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError';
import { mapGallerySearchResult, mapSearchSuggestion } from '@/transformers/search';
import type { SearchVerificationError } from '@/services/SearchApi';
import type {
  GallerySearchResult,
  PaginationMeta,
  SearchAppliedFilters,
  SearchFiltersResponse,
  SearchGalleriesResponse,
  SearchSuggestion,
  SearchTrendingCategory,
} from '@/types';

type SearchExecutionOptions = {
  page?: number;
  perPage?: number;
  turnstileToken?: string;
};

const defaultPagination: PaginationMeta = {
  current_page: 1,
  from: null,
  last_page: 1,
  per_page: 12,
  to: null,
  total: 0,
};


export const useSearchStore = defineStore('search', () => {
  const query = ref('');
  const results = ref<GallerySearchResult[]>([]);
  const suggestions = ref<SearchSuggestion[]>([]);
  const filters = ref<SearchFiltersResponse | null>(null);
  const appliedFilters = ref<SearchAppliedFilters>({});
  const pagination = ref<PaginationMeta>(defaultPagination);

  const trending = ref<SearchTrendingCategory[]>([]);
  const popular = ref<GallerySearchResult[]>([]);

  const loading = ref(false);
  const error = ref<string | null>(null);
  const requiresVerification = ref(false);
  const turnstileSiteKey = ref<string | null>(null);
  const retryAfter = ref<number | null>(null);
  const verified = ref(false);

  const pendingSearch = ref<{
    query: string;
    filters: SearchAppliedFilters;
    page: number;
    perPage: number;
  } | null>(null);
  const pendingSuggestionQuery = ref<string | null>(null);

  const handleVerificationRequired = (siteKey: string | null): void => {
    requiresVerification.value = true;
    turnstileSiteKey.value = siteKey;
  };

  const clearVerificationState = (): void => {
    requiresVerification.value = false;
    turnstileSiteKey.value = null;
    retryAfter.value = null;
  };

  const extractRetryAfter = (errorData: SearchVerificationError): number | null => {
    if (typeof errorData.retry_after === 'number') {
      return errorData.retry_after;
    }

    return null;
  };

  const fetchSuggestions = async (searchQuery: string, turnstileToken?: string): Promise<SearchSuggestion[]> => {
    if (searchQuery.trim().length < 3) {
      suggestions.value = [];
      pendingSuggestionQuery.value = null;
      return [];
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await searchApi.fetchSuggestions(searchQuery, turnstileToken);

      suggestions.value = response.data.map(mapSearchSuggestion).filter((item) => item.title.length > 0);
      clearVerificationState();
      pendingSuggestionQuery.value = null;
      return suggestions.value;
    } catch (err: unknown) {
      const status = getApiErrorStatus(err);
      const data = ((err as { data?: SearchVerificationError })?.data ?? {}) as SearchVerificationError;

      if (status === 403 && data.requires_verification) {
        pendingSuggestionQuery.value = searchQuery;
        handleVerificationRequired(data.turnstile_site_key ?? null);
        return [];
      }

      if (status === 429) {
        retryAfter.value = extractRetryAfter(data);
      }

      error.value = getApiErrorMessage(err, 'Unable to fetch suggestions');
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const executeSearch = async (
    searchQuery: string,
    filtersPayload: SearchAppliedFilters,
    options: SearchExecutionOptions = {}
  ): Promise<SearchGalleriesResponse | null> => {
    const page = options.page ?? pagination.value.current_page ?? 1;
    const perPage = options.perPage ?? pagination.value.per_page ?? 12;

    query.value = searchQuery;
    appliedFilters.value = { ...filtersPayload };
    loading.value = true;
    error.value = null;

    try {
      const response = await searchApi.searchGalleries(
        searchQuery,
        filtersPayload,
        page,
        perPage,
        options.turnstileToken
      );

      results.value = response.data.map(mapGallerySearchResult);
      pagination.value = {
        ...defaultPagination,
        ...response.meta,
        per_page: Number(response.meta.per_page ?? perPage),
        current_page: Number(response.meta.current_page ?? page),
      };

      if (response.filters) {
        filters.value = response.filters;
      }

      pendingSearch.value = null;
      clearVerificationState();

      return {
        ...response,
        data: results.value,
      };
    } catch (err: unknown) {
      const status = getApiErrorStatus(err);
      const data = ((err as { data?: SearchVerificationError })?.data ?? {}) as SearchVerificationError;

      if (status === 403 && data.requires_verification) {
        pendingSearch.value = {
          query: searchQuery,
          filters: { ...filtersPayload },
          page,
          perPage,
        };

        handleVerificationRequired(data.turnstile_site_key ?? null);
        return null;
      }

      if (status === 429) {
        retryAfter.value = extractRetryAfter(data);
      }

      error.value = getApiErrorMessage(err, 'Unable to execute search');
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchAvailableFilters = async (): Promise<SearchFiltersResponse | null> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await searchApi.fetchAvailableFilters();
      filters.value = response;
      return response;
    } catch (err: unknown) {
      error.value = getApiErrorMessage(err, 'Unable to fetch available filters');
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchTrending = async (limit = 6): Promise<SearchTrendingCategory[]> => {
    const response = await searchApi.fetchTrending(limit);

    trending.value = response.data;
    return trending.value;
  };

  const fetchPopular = async (limit = 6): Promise<GallerySearchResult[]> => {
    const response = await searchApi.fetchPopular(limit);

    popular.value = response.data.map(mapGallerySearchResult);
    return popular.value;
  };

  const verifyWithTurnstile = async (token: string): Promise<void> => {
    verified.value = true;
    clearVerificationState();

    const pendingSearchRequest = pendingSearch.value;
    const pendingSuggestion = pendingSuggestionQuery.value;

    if (pendingSearchRequest) {
      await executeSearch(pendingSearchRequest.query, pendingSearchRequest.filters, {
        page: pendingSearchRequest.page,
        perPage: pendingSearchRequest.perPage,
        turnstileToken: token,
      });
    }

    if (pendingSuggestion) {
      await fetchSuggestions(pendingSuggestion, token);
    }
  };

  return {
    query,
    results,
    suggestions,
    filters,
    appliedFilters,
    pagination,
    trending,
    popular,
    loading,
    error,
    requiresVerification,
    turnstileSiteKey,
    retryAfter,
    verified,
    fetchSuggestions,
    executeSearch,
    fetchAvailableFilters,
    fetchTrending,
    fetchPopular,
    verifyWithTurnstile,
    handleVerificationRequired,
  };
});
